import { useState, useEffect, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUser, faMobileScreen, faIdCard, faUserPen, faBuilding } from "@fortawesome/free-solid-svg-icons";
import CrossIcon from "../../../assets/icons/maid-module-icons/CrossIcon.svg";
import BackArrow from "../../../assets/icons/maid-module-icons/BackArrow.svg";
import MobileNumber from "./components/Mobile";
import Details from "./components/Details";
import Document from "./components/Document";
import Identity from "./components/SelectedDocument";
import AddFlat from "./components/AddFlat";
import ServiceStaffModal from "../modals/modals/ServiceStaffModal.js";
import { toast, ToastContainer } from "react-toastify";


const AddMaidModal = ({ show = false, onClose, initialRole = null, editData = null }) => {
  const isEditMode = !!editData;

  const [step, setStep] = useState(1);
  const [showServiceModal, setShowServiceModal] = useState(false);
  const [isFlatSelectorOpen, setIsFlatSelectorOpen] = useState(false);

  const [phoneDigits, setPhoneDigits] = useState(Array(10).fill(""));
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [documentDetails, setDocumentDetails] = useState({ type: null, number: "", file: null, isMasked: false });
  const [name, setName] = useState("");
  const [gender, setGender] = useState("");
  const [vehicleType, setVehicleType] = useState("");
  const [vehicleNumber, setVehicleNumber] = useState("");
  const [profileImage, setProfileImage] = useState(null);
  const [selectedFlats, setSelectedFlats] = useState([]);

  const identityRef = useRef(null);
  const [identityResetKey, setIdentityResetKey] = useState(0);

  const roleName = initialRole?.roleName || "Staff";
  const mobileNumber = phoneDigits.join("").trim();
  const isMobileComplete = mobileNumber.length === 10;
  const showSkipButton = step === 1 && !isMobileComplete;

  const STEPS = [
    { label: roleName, icon: faUser },
    { label: "Mobile", icon: faMobileScreen },
    { label: "Identity", icon: faIdCard },
    { label: "Name", icon: faUserPen },
    { label: "Flat", icon: faBuilding },
  ];

  const contentRef = useRef(null);
  useEffect(() => {
    if (contentRef.current) {
      contentRef.current.scrollTop = 0;
    }
  }, [step]);

  useEffect(() => {
    if (!show) return;

    setShowServiceModal(false);
    setIsFlatSelectorOpen(false);

    if (isEditMode && editData) {
      setStep(1);

      const digits = (editData.mobileNumber || "").replace(/\D/g, "").slice(0, 10).split("");
      const paddedDigits = [...digits, ...Array(10).fill("")].slice(0, 10);
      setPhoneDigits(paddedDigits);

      setSelectedDocument(editData.documentType || null);
      setDocumentDetails({
        type: editData.documentType || null,
        number: editData.documentNumber || "",
        file: null,
        isMasked: editData.documentIsMasked || false,
        previewBase64: editData.documentPreviewBase64 || null,
      });

      setName(editData.name || "");
      setGender(editData.gender || "");
      setVehicleType(editData.vehicleType || "");
      setVehicleNumber(editData.vehicleNumber || "");
      setProfileImage(editData.profileImage || null);
      setSelectedFlats(editData.selectedFlats || []);

    } else {
      setStep(1);
      setPhoneDigits(Array(10).fill(""));
      setSelectedDocument(null);
      setDocumentDetails({ type: null, number: "", file: null, isMasked: false });
      setName("");
      setGender("");
      setVehicleType("");
      setVehicleNumber("");
      setProfileImage(null);
      setSelectedFlats([]);
    }
  }, [show]);

  const isVehicleValid = () => {
    const hasType   = !!vehicleType;
    const hasNumber = vehicleNumber?.trim().length > 0;
    return hasType === hasNumber;
  };

  const isDocumentValid = () => {
    const num = (documentDetails.number || "").toString().trim();
    if (!num) return false;

    const docId    = (selectedDocument?.id    || "").toLowerCase();
    const docLabel = (selectedDocument?.label || "").toLowerCase();
    const isAadhaar = docId === "aadhaar" || docLabel.includes("aadhar") || docLabel.includes("aadhaar");
    const isPAN     = docId === "pan"     || docLabel.includes("pan");
    const isVoter   = docLabel.includes("voter") || docLabel.includes("epic");

    if (documentDetails.isMasked) {
      if (num.length !== 4) return false;
      if (isAadhaar) return /^\d{4}$/.test(num);
      if (isPAN)     return /^\d{3}[A-Z]$/.test(num);
      if (isVoter)   return /^[A-Z0-9]{4}$/.test(num);
      return /^\d{4}$/.test(num);
    }

    if (isAadhaar) return /^\d{12}$/.test(num);
    if (isPAN)     return /^[A-Z]{5}[0-9]{4}[A-Z]$/.test(num);
    if (isVoter)   return /^[A-Z]{3}[0-9]{7}$/.test(num);
    return num.length >= 4;
  };

  const hasDocumentPhoto = () =>
    !!documentDetails.file || !!documentDetails.previewBase64;

  const canProceed = () => {
    switch (step) {
      case 1: return true;
      case 2: return !!selectedDocument;
      case 3: return true;
      case 4: return name.trim() && gender && !!profileImage && isVehicleValid();
      case 5: return selectedFlats.length > 0;
      default: return true;
    }
  };

  const getValidationMessage = () => {
    switch (step) {
      case 2: return "Please select a document type.";
      case 3: return "Please enter document number and upload photo.";
      case 4: {
        if (!name.trim())    return "Please enter full name.";
        if (!gender)         return "Please select gender.";
        if (!profileImage)   return "Please upload a profile photo.";
        if (!isVehicleValid()) {
          if (vehicleType  && !vehicleNumber?.trim()) return "Vehicle number is required when vehicle type is selected.";
          if (!vehicleType &&  vehicleNumber?.trim()) return "Vehicle type is required when vehicle number is entered.";
        }
        return "Please complete all required fields.";
      }
      case 5: return "Please select at least one flat.";
      default: return "Please complete required fields.";
    }
  };

  const handleNext = () => {
    if (step === 3) {
      const isValid = identityRef.current?.validateBeforeContinue();
      if (!isValid) return;
    }

    if (!canProceed()) {
      toast.warn(getValidationMessage(), { position: "top-center", autoClose: 3000 });
      return;
    }

    if (step === STEPS.length) {
      setShowServiceModal(true);
    } else {
      setStep((s) => s + 1);
    }
  };

  const handleBack = () => setStep((s) => s - 1);

  if (!show) return null;

  const identityCurrentDetails = {
    number        : documentDetails.number,
    isMasked      : documentDetails.isMasked,
    previewBase64 : documentDetails.previewBase64 || null,
    hasPhoto      : hasDocumentPhoto(),
  };

  return (
    <>
      <ToastContainer />

      <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 1060 }} />

      <div style={{ position: "fixed", inset: 0, zIndex: 1070, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div
          className="bg-white d-flex flex-column"
          style={{ width: "50%", maxWidth: 600, height: "96vh", borderRadius: 18, overflow: "hidden", fontFamily: "Inter, sans-serif" }}
        >
          {/* Header */}
          <div className="d-flex justify-content-between align-items-center px-4 py-3 text-white" style={{ backgroundColor: "#1C80D2" }}>
            <h5 className="mb-0" style={{ fontWeight: 600 }}>
              {isEditMode ? `Edit ${roleName}` : `Add ${roleName}`}
            </h5>
            <button
              className="btn p-0 d-flex align-items-center justify-content-center bg-white"
              style={{ width: 24, height: 24, borderRadius: 6 }}
              onClick={onClose}
            >
              <img src={CrossIcon} alt="close" />
            </button>
          </div>

          {/* Progress Steps */}
          <div className="px-1 pt-4">
            <div className="d-flex position-relative">
              {STEPS.map((s, i) => {
                const active = i === step - 1;
                const filled = i < step;
                return (
                  <div key={i} className="text-center position-relative" style={{ flex: 1 }}>
                    {i !== 0 && (
                      <div style={{ position: "absolute", top: 18, left: "-50%", width: "100%", height: 3, background: "#dee2e6", zIndex: 0 }}>
                        <div style={{ height: "100%", width: filled ? "100%" : "0%", background: "#1C80D2", borderRadius: 2, transition: "width 0.4s ease" }} />
                      </div>
                    )}
                    <div style={{
                      width: 36, height: 36, borderRadius: "50%",
                      background: filled ? "#1C80D2" : "#F8F8F8",
                      color: filled ? "#fff" : "#6c757d",
                      border: `2px solid ${filled ? "#1C80D2" : "#dee2e6"}`,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      margin: "0 auto", position: "relative", zIndex: 1, transition: "all 0.3s ease",
                    }}>
                      <FontAwesomeIcon icon={s.icon} />
                    </div>
                    <small style={{
                      display: "block", marginTop: 8,
                      color: filled || active ? "#1C80D2" : "#6c757d",
                      fontWeight: active ? 600 : 400,
                      margin: "8px auto 0", textAlign: "center",
                      wordBreak: "break-word", lineHeight: "1.2",
                    }}>
                      {s.label}
                    </small>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Content */}
          <div
            ref={contentRef}
            className="flex-grow-1 px-5 py-4"
            style={{ overflowY: "auto", display: "flex", flexDirection: "column" }}
          >
            {step > 1 && !(step === STEPS.length && isFlatSelectorOpen) && (
              <button
                className="btn mb-3 d-flex align-items-center justify-content-center"
                style={{ width: 36, height: 36, padding: 10, backgroundColor: "#1C80D2", border: "none", borderRadius: "50%", alignSelf: "flex-start" }}
                onClick={handleBack}
              >
                <img src={BackArrow} alt="back" style={{ width: 18, height: 18 }} />
              </button>
            )}

            <div style={{ display: step === 1 ? "block" : "none" }}>
              <MobileNumber phoneDigits={phoneDigits} setPhoneDigits={setPhoneDigits} />
            </div>

            <div style={{ display: step === 2 ? "block" : "none" }}>
              <Document
                selectedDocument={selectedDocument}
                setSelectedDocument={(doc) => {
                  if (doc?.id !== selectedDocument?.id) {
                    setDocumentDetails({ type: null, number: "", file: null, isMasked: false, previewBase64: null });
                    setIdentityResetKey(k => k + 1);
                  }
                  setSelectedDocument(doc);
                }}
              />
            </div>

            <div style={{ display: step === 3 ? "block" : "none" }}>
              <Identity
                key={identityResetKey}
                ref={identityRef}
                selectedDocument={selectedDocument}
                setDocumentDetails={setDocumentDetails}
                currentDetails={identityCurrentDetails}
              />
            </div>

            <div style={{ display: step === 4 ? "block" : "none" }}>
              <Details
                profileImage={profileImage} setProfileImage={setProfileImage}
                name={name} setName={setName}
                gender={gender} setGender={setGender}
                vehicleType={vehicleType} setVehicleType={setVehicleType}
                vehicleNumber={vehicleNumber} setVehicleNumber={setVehicleNumber}
              />
            </div>

            <div style={{ display: step === 5 ? "block" : "none" }}>
              <AddFlat
                selectedFlats={selectedFlats} setSelectedFlats={setSelectedFlats}
                setIsFlatSelectorOpen={setIsFlatSelectorOpen}
                onComplete={handleNext}
              />
            </div>
          </div>

          {/* Footer  */}
          {!isFlatSelectorOpen && (
            <div className="px-4 pb-4 mt-auto">
              <button
                className="btn w-100 text-white"
                style={{
                  background  : canProceed() ? "#1C80D2" : "#a0c4ff",
                  borderRadius: 30,
                  padding     : "14px",
                  fontWeight  : canProceed() ? 600 : 500,
                  cursor      : canProceed() ? "pointer" : "not-allowed",
                }}
                onClick={() => {
                  if (step === 1 && showSkipButton) setPhoneDigits(Array(10).fill(""));
                  handleNext();
                }}
                disabled={!canProceed() && !(step === 1 && showSkipButton)}
              >
                {step === STEPS.length
                  ? (isEditMode ? "Save Changes" : "Continue")
                  : step === 1 && showSkipButton
                    ? "Skip for now"
                    : "Continue"}
              </button>
            </div>
          )}
        </div>
      </div>

      <ServiceStaffModal
        show={showServiceModal}
        onHide={() => setShowServiceModal(false)}
        staffData={{
          staffId: editData?.staffId || null,
          isEditMode,
          name,
          mobileNumber,
          gender,
          vehicleType,
          vehicleNumber,
          flats: selectedFlats.map((f) => f.flatName || f.flatId).join(", "),
          documentType : selectedDocument?.label,
          documentNumber: documentDetails.number,
          profileImage,
          documentFile: documentDetails.file,
          documentPreviewBase64: documentDetails.previewBase64 || null,
          selectedDocumentId : selectedDocument?.id,
          selectedFlats,
          roleId : initialRole?.roleId || 0,
          roleName,
        }}
        onSuccess={() => { setShowServiceModal(false); onClose(); }}
      />
    </>
  );
};

export default AddMaidModal;